/**
 * W1-11a (app-registry-keymap): `settings: SettingsService` (DESIGN.md §4.20, §5.15; contract app/settings/types.ts).
 *
 * - Defaults are usable immediately (`createDefaultSettings`, locale from navigator.language); `ready` resolves
 *   once `attach(persist)` has read the user's values from the persist kv store under "settings/v1" (also when that
 *   read failed: it is retried in the background and the values apply live when it succeeds).
 * - Stored data is the DIFFERENCE from the defaults (only what the user changed, plus every `ext` preference), so a
 *   later change of a product default reaches users who never touched that setting; it is validated against
 *   SETTINGS_SCHEMA on load and on every write (numbers clamped, enums checked).
 * - Every change applies live: subscribers get the new immutable value synchronously. Writes are debounced and
 *   flushed when the page is hidden. Changes made before the load completes win over the stored values.
 * - Nothing is written before the stored values were read: the stored blob is the whole user configuration, so a
 *   write after a failed read would replace it with only the new changes. After a failed read every write first
 *   re-reads (and merges, local changes winning); failed reads and writes are retried with a backoff.
 * - Tabs stay in sync through a BroadcastChannel (when available): another tab's saved settings are applied live.
 * - `reset()` restores every default (keeping "first run done", so the first-run question is not asked again),
 *   `reset('section.key')` one setting, `reset('ext.<feature>.<key>')` removes one feature preference.
 */

import type { Json, Unsubscribe } from '../../core/util/types';
import type { PersistClient } from '../../persist/types';
import {
  createDefaultSettings,
  firstRunSettings,
  sanitizeSettings,
  sanitizeValue,
  specOf,
  type FirstRunAnswer,
} from './defaults';
import type { SettingPath, SettingsSection, SettingsService, UserSettings } from './types';

/** kv key of the user's settings (contract: persisted per user under "settings/v1"). */
export const SETTINGS_KV_KEY = 'settings/v1';

/** Longest delay between retries of a failed read or write. */
const MAX_RETRY_DELAY_MS = 30_000;

/** The part of the persist client the settings use. */
export type SettingsPersistence = Pick<PersistClient, 'kvGet' | 'kvSet'>;

export interface AppSettings extends SettingsService {
  /** Load the stored settings and persist every later change through this client. Resolves after the first read. */
  attach(persist: SettingsPersistence): Promise<void>;
  /** Several changes as one update (one notification, one write). */
  update(fn: (s: UserSettings) => UserSettings): void;
  /** Apply the first-run question's answer (DESIGN.md §4.2) and mark the first run done. */
  applyFirstRun(answer: FirstRunAnswer): void;
  /** The defaults this service resets to. */
  defaults(): UserSettings;
  /** Default value of a setting (undefined for unknown paths and unset `ext` keys). */
  defaultOf(path: SettingPath): unknown;
  /** True when the setting has its default value (Settings page: Reset disabled). */
  isDefault(path: SettingPath): boolean;
  /** True once the stored values were read (writes are held until then). */
  readonly loaded: boolean;
  /** 'idle' before `attach`, then 'loading', 'loaded', or 'failed' (the stored values could not be read yet). */
  readonly loadState: SettingsLoadState;
  /** Write pending changes now. */
  flush(): Promise<void>;
  dispose(): void;
}

export type SettingsLoadState = 'idle' | 'loading' | 'loaded' | 'failed';

export interface SettingsServiceOptions {
  /** Defaults (default: product defaults with the browser locale). */
  readonly defaults?: UserSettings | (() => UserSettings);
  /** Debounce of kv writes in ms (default 300). */
  readonly saveDelayMs?: number;
  /** First retry delay after a failed read or write in ms, doubled per failure up to 30 s (default 2000). */
  readonly retryDelayMs?: number;
  /** kv key (default SETTINGS_KV_KEY). */
  readonly key?: string;
  /** BroadcastChannel name for cross-tab sync; null disables it (default null). */
  readonly broadcast?: string | null;
  /** Reports load/save failures (default console.error). */
  readonly onError?: (err: unknown, what: 'load' | 'save') => void;
}

type Section = Exclude<SettingsSection, 'ext'>;

function isRecord(v: unknown): v is Readonly<Record<string, unknown>> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Structural equality of JSON-like values. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a)) {
    return Array.isArray(b) && a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  }
  if (isRecord(a) && isRecord(b)) {
    const ka = Object.keys(a).filter((k) => a[k] !== undefined);
    const kb = Object.keys(b).filter((k) => b[k] !== undefined);
    return ka.length === kb.length && ka.every((k) => Object.hasOwn(b, k) && deepEqual(a[k], b[k]));
  }
  return false;
}

const SECTIONS: readonly Section[] = ['general', 'editing', 'audio', 'preview', 'keyboard', 'checker'];

/** What is stored: settings that differ from the defaults, and every ext preference. */
export function settingsDiff(value: UserSettings, defaults: UserSettings): Json {
  const out: Record<string, Json> = {};
  for (const section of SECTIONS) {
    const cur = value[section] as Readonly<Record<string, unknown>>;
    const def = defaults[section] as Readonly<Record<string, unknown>>;
    const changed: Record<string, Json> = {};
    for (const key of Object.keys(def)) {
      if (!deepEqual(cur[key], def[key])) changed[key] = cur[key] as Json;
    }
    if (Object.keys(changed).length > 0) out[section] = changed;
  }
  if (Object.keys(value.ext).length > 0) out.ext = value.ext;
  return out;
}

function splitPath(path: string): { readonly section: string; readonly key: string } | null {
  const dot = path.indexOf('.');
  if (dot <= 0 || dot === path.length - 1) return null;
  return { section: path.slice(0, dot), key: path.slice(dot + 1) };
}

/** Value at a path of a settings object (undefined for unknown paths). */
function valueAt(s: UserSettings, path: string): unknown {
  const p = splitPath(path);
  if (p === null) return undefined;
  if (p.section === 'ext') return s.ext[p.key];
  if (!(SECTIONS as readonly string[]).includes(p.section)) return undefined;
  return (s[p.section as Section] as Readonly<Record<string, unknown>>)[p.key];
}

/** Copy of `s` with `path` set to `v` (undefined removes an ext key); unknown paths leave `s` unchanged. */
function withValue(s: UserSettings, path: string, v: unknown): UserSettings {
  const p = splitPath(path);
  if (p === null) return s;
  if (p.section === 'ext') {
    const rest = Object.fromEntries(Object.entries(s.ext).filter(([k]) => k !== p.key));
    return { ...s, ext: v === undefined ? rest : { ...rest, [p.key]: v as Json } };
  }
  if (specOf(p.section, p.key) === undefined) return s;
  const section = p.section as Section;
  return { ...s, [section]: { ...s[section], [p.key]: v } };
}

/** Every `section.key` and `ext.*` path whose value differs between two settings objects. */
function changedPaths(a: UserSettings, b: UserSettings): string[] {
  const out: string[] = [];
  for (const section of SECTIONS) {
    if (a[section] === b[section]) continue;
    const sa = a[section] as Readonly<Record<string, unknown>>;
    const sb = b[section] as Readonly<Record<string, unknown>>;
    for (const key of Object.keys(sb)) if (!deepEqual(sa[key], sb[key])) out.push(`${section}.${key}`);
  }
  if (a.ext !== b.ext) {
    for (const key of new Set([...Object.keys(a.ext), ...Object.keys(b.ext)])) {
      if (!deepEqual(a.ext[key], b.ext[key])) out.push(`ext.${key}`);
    }
  }
  return out;
}

export function createSettingsService(opts: SettingsServiceOptions = {}): AppSettings {
  const defaults: UserSettings =
    typeof opts.defaults === 'function' ? opts.defaults() : (opts.defaults ?? createDefaultSettings());
  const key = opts.key ?? SETTINGS_KV_KEY;
  const saveDelay = opts.saveDelayMs ?? 300;
  const report =
    opts.onError ??
    ((err: unknown, what: 'load' | 'save') => {
      console.error(`settings: ${what} failed`, err);
    });
  const retryDelay = opts.retryDelayMs ?? 2000;
  const instanceId = Math.random().toString(36).slice(2);

  let value: UserSettings = defaults;
  let persist: SettingsPersistence | null = null;
  let loadState: SettingsLoadState = 'idle';
  /** Paths changed locally and not yet saved, with the change generation (they win over stored or broadcast values). */
  const unsaved = new Map<string, number>();
  let generation = 0;
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Consecutive failed reads or writes (retry backoff). */
  let failures = 0;
  let saving: Promise<void> = Promise.resolve();
  const listeners = new Set<(s: UserSettings) => void>();
  let resolveReady: () => void = () => undefined;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });

  let channel: BroadcastChannel | null = null;
  const onPageHide = (): void => {
    void flush();
  };
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') void flush();
  };

  const notify = (): void => {
    const snapshot = value;
    for (const cb of [...listeners]) {
      try {
        cb(snapshot);
      } catch (err) {
        console.error('settings: subscriber failed', err);
      }
    }
  };

  const clearRetry = (): void => {
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
  };

  /** Read the stored values and rebase onto them (local unsaved changes win); false when the read failed. */
  const load = async (client: SettingsPersistence): Promise<boolean> => {
    let stored: Json | undefined;
    try {
      stored = await client.kvGet(key);
    } catch (err) {
      report(err, 'load');
      if (persist === client) loadState = 'failed';
      return false;
    }
    if (persist !== client) return false;
    rebase(stored);
    loadState = 'loaded';
    failures = 0;
    clearRetry();
    return true;
  };

  /**
   * One persistence step, serialised on `saving`: after a failed read it re-reads first (a write never replaces
   * stored values it has not seen), then writes the pending changes.
   */
  const sync = async (): Promise<void> => {
    const target = persist;
    if (target === null || loadState === 'idle' || loadState === 'loading') return;
    if (loadState === 'failed' && !(await load(target))) {
      scheduleRetry();
      return;
    }
    if (persist !== target || unsaved.size === 0) return;
    const diff = settingsDiff(value, defaults);
    const upTo = generation;
    try {
      await target.kvSet(key, diff);
      failures = 0;
      clearRetry();
      for (const [path, gen] of [...unsaved]) if (gen <= upTo) unsaved.delete(path);
      channel?.postMessage({ from: instanceId, diff });
    } catch (err) {
      // Keep the paths unsaved: the retry, the next change or a flush writes them again.
      report(err, 'save');
      scheduleRetry();
    }
  };

  const flush = (): Promise<void> => {
    if (saveTimer !== null) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    if (unsaved.size === 0 && loadState !== 'failed') return saving;
    saving = saving.then(sync);
    return saving;
  };

  /** Retry a failed read or write with a doubling delay (2 s, 4 s, ... 30 s). */
  const scheduleRetry = (): void => {
    if (persist === null || retryTimer !== null) return;
    failures++;
    const delay = Math.min(retryDelay * 2 ** Math.min(failures - 1, 16), MAX_RETRY_DELAY_MS);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      saving = saving.then(sync);
    }, delay);
  };

  const scheduleSave = (): void => {
    if (persist === null || loadState === 'idle' || loadState === 'loading') return;
    if (saveTimer !== null) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void flush();
    }, saveDelay);
  };

  /** `value` with the given paths taken from `target` (untouched sections keep their identity). */
  const applyPaths = (target: UserSettings, paths: readonly string[]): UserSettings => {
    let next = value;
    for (const p of paths) next = withValue(next, p, valueAt(target, p));
    return next;
  };

  /** Adopt a candidate value (already sanitized): marks changed paths unsaved, notifies, schedules a write. */
  const commit = (candidate: UserSettings): void => {
    const paths = changedPaths(value, candidate);
    if (paths.length === 0) return;
    value = applyPaths(candidate, paths);
    generation++;
    for (const p of paths) unsaved.set(p, generation);
    notify();
    scheduleSave();
  };

  /** Rebase onto values from storage or another tab, keeping local unsaved changes. */
  const rebase = (storedDiff: unknown): void => {
    let target = sanitizeSettings(storedDiff, defaults);
    for (const p of unsaved.keys()) target = withValue(target, p, valueAt(value, p));
    const paths = changedPaths(value, target);
    if (paths.length === 0) return;
    value = applyPaths(target, paths);
    notify();
  };

  const sanitizeAt = (section: string, k: string, v: unknown): unknown => {
    const spec = specOf(section, k);
    if (spec === undefined) return v;
    return sanitizeValue(spec, v, valueAt(value, `${section}.${k}`));
  };

  const set: SettingsService['set'] = (section, k, v) => {
    if (section === 'ext') {
      service.setExt(String(k), v as Json);
      return;
    }
    const path = `${section}.${String(k)}`;
    if (specOf(section, String(k)) === undefined) {
      console.warn(`settings: unknown setting ${path}`);
      return;
    }
    commit(withValue(value, path, sanitizeAt(section, String(k), v)));
  };

  const service: AppSettings = {
    get value() {
      return value;
    },
    get: (section, k) => value[section][k],
    set,
    getExt: (k) => value.ext[k],
    setExt(k, v) {
      if (deepEqual(value.ext[k], v)) return;
      commit(withValue(value, `ext.${k}`, v));
    },
    reset(path) {
      if (path === undefined) {
        commit({ ...defaults, general: { ...defaults.general, firstRunDone: value.general.firstRunDone } });
        return;
      }
      const p = splitPath(path);
      if (p === null) return;
      if (p.section === 'ext') {
        commit(withValue(value, path, defaults.ext[p.key]));
        return;
      }
      if (specOf(p.section, p.key) === undefined) {
        console.warn(`settings: unknown setting ${path}`);
        return;
      }
      commit(withValue(value, path, valueAt(defaults, path)));
    },
    subscribe(cb): Unsubscribe {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    ready,
    async attach(client) {
      persist = client;
      loadState = 'loading';
      clearRetry();
      failures = 0;
      const ok = await load(client);
      if (persist !== client) return;
      if (!ok) scheduleRetry();
      else if (unsaved.size > 0) scheduleSave();
      if (channel === null && opts.broadcast != null && typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(opts.broadcast);
        channel.onmessage = (e: MessageEvent<unknown>) => {
          const data = e.data;
          if (!isRecord(data) || data.from === instanceId) return;
          rebase(data.diff);
          // Another tab just wrote this diff: it is what the store holds, so a failed read is no longer a risk.
          if (loadState === 'failed' && persist !== null) {
            loadState = 'loaded';
            failures = 0;
            clearRetry();
            if (unsaved.size > 0) scheduleSave();
          }
        };
      }
      if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', onPageHide);
        document.addEventListener('visibilitychange', onVisibility);
      }
      resolveReady();
    },
    update(fn) {
      commit(sanitizeSettings(settingsDiff(fn(value), defaults), defaults));
    },
    applyFirstRun(answer) {
      service.update((s) => firstRunSettings(s, answer));
    },
    defaults: () => defaults,
    defaultOf: (path) => valueAt(defaults, path),
    isDefault: (path) => deepEqual(valueAt(value, path), valueAt(defaults, path)),
    get loaded() {
      return loadState === 'loaded';
    },
    get loadState() {
      return loadState;
    },
    flush,
    dispose() {
      if (saveTimer !== null) clearTimeout(saveTimer);
      saveTimer = null;
      clearRetry();
      channel?.close();
      channel = null;
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', onPageHide);
        document.removeEventListener('visibilitychange', onVisibility);
      }
      listeners.clear();
      persist = null;
    },
  };
  return service;
}

/** The app's settings (cross-tab sync on). W1-16 calls `settings.attach(persistClient)` at startup. */
export const settings: AppSettings = createSettingsService({ broadcast: 'phiedit-settings' });
